// Page-level translations: extends the base Translations interface with sections
// for each app page. AR + EN values must always stay in sync.

export interface PageTranslations {
  commonExt: {
    clearFilters: string; viewDetails: string; refresh: string;
    copyId: string; editData: string; active: string; inactive: string;
    suspended: string; name: string; email: string; phone: string;
    status: string; type: string; updateSuccess: string; updateError: string;
    refreshSuccess: string; close: string; add: string; create: string;
    print: string; exportLabel: string; saveChanges: string; testConnection: string;
    upload: string; required: string; notSpecified: string; pending: string;
    yes: string; no: string; deleteConfirm: string; cannotUndo: string; copy: string;
  };
  dashboard: { welcomeMessage: string };
  drivers: {
    pageTitle: string; pageSubtitle: string; addDriverButton: string;
    tableHeaderName: string; tableHeaderPhone: string; tableHeaderPaymentType: string;
    paymentTypeCommission: string; paymentTypeSalary: string;
    tableHeaderCommissionRate: string; tableHeaderCreatedDate: string;
    viewDetailsTooltip: string; emptyStateTitle: string; emptyStateDescription: string;
    driverIdCopied: string; driverNotFound: string; driverNotFoundDescription: string;
    backToListButton: string; statusActive: string; statusInactive: string;
    copyIdButton: string; currentShipmentsLabel: string; shipmentUnitLabel: string;
    availableLabel: string; usedCapacityLabel: string; cubicMeterUnit: string;
    usedWeightLabel: string; kilogramUnit: string;
    tabOverview: string; tabZones: string; tabRoutes: string; tabShipments: string;
    tabLocation: string; tabHistory: string; tabFinancial: string;
    editDataMenuItem: string; disableDriverMenuItem: string; enableDriverMenuItem: string;
    assignShipmentMenuItem: string; shipmentsListTitle: string; shipmentsListDescription: string;
    viewShipmentsButton: string; trackLocationTitle: string; trackLocationDescription: string;
    activityHistoryTitle: string; activityHistoryDescription: string;
    errorLoadingDriverData: string; delegateLabel: string; latitudeLabel: string;
    longitudeLabel: string; lastUpdateLabel: string; trackingPageTitle: string;
    connectedStatus: string; disconnectedStatus: string; activeDriversLabel: string;
    noActiveDrivers: string; sinceLabel: string; activeDriverStat: string;
    secondsUnit: string; minuteUnit: string; hourUnit: string;
  };
  shipments: {
    pageTitle: string; pageSubtitle: string; addNew: string;
    trackingNumber: string; status: string; address: string; recipient: string;
    serviceType: string; value: string; date: string; viewDetails: string;
    emptyTitle: string; emptyDescription: string; clearFilters: string;
    pagination: string; previous: string; next: string;
  };
  shipmentDetails: {
    back: string; copy: string; print: string; retryDelivery: string; assignDriver: string;
    deliveredBadge: string; driverSettled: string; tenantSettled: string;
    codCollection: string; pending: string; carrier: string; fromCity: string; toCity: string;
    piecesCount: string; createdDate: string; expectedDeliveryDate: string;
    actualDeliveryDate: string; serviceType: string; paymentType: string; paymentStatus: string;
    codAmount: string; shipmentValue: string; platformFee: string; deliveryFees: string;
    tenantPayout: string; total: string; shipmentInfo: string; customer: string;
    sender: string; warehouseLocation: string; senderAddress: string;
    recipientAddress: string; items: string; itemName: string; itemQuantity: string;
    itemSku: string; trips: string; loading: string; noTrips: string;
    tripSequence: string; tripType: string; tripRoute: string; tripDriver: string;
    manifest: string; attempt: string; unassigned: string; viewTrip: string;
    descriptionTab: string; historyTab: string; notificationsTab: string;
    timeline: string; noHistory: string; noNotifications: string;
    notificationsCount: string; notificationType: string; notificationChannels: string;
    notificationRecipient: string; notificationTitle: string;
    channelInApp: string; channelWhatsapp: string; channelEmail: string; channelSms: string;
    recipientRoleTenant: string; recipientRoleCarrier: string; recipientRoleDriver: string;
    recipientRoleAdmin: string; recipientRoleFinal: string;
    assignDriverTitle: string; noDriversAvailable: string; currentShipments: string;
    coverageAreas: string; retryDialogTitle: string; retryWarning: string;
    retryScheduledDate: string; retryNotes: string; retryNotesPlaceholder: string;
    cancel: string; scheduling: string; scheduleRetry: string;
    trackingCopied: string; failedLoadingDrivers: string; failedLoadingNotifications: string;
    failedLoadingHistory: string; driverAssignedSuccess: string; failedAssignDriver: string;
    retryScheduledSuccess: string; outForPickup: string; collectedFromSender: string;
    inTransit: string; outForDelivery: string; delivered: string; deliveryFailed: string;
    returning: string; returned: string; pendingStatus: string; totalTrips: string;
  };
  createShipment: {
    pageTitle: string; pageSubtitle: string; shipmentSection: string;
    customer: string; selectCustomer: string; search: string; required: string;
    selectCarrier: string; serviceType: string; selectServiceType: string;
    warehouse: string; loading: string; selectWarehouse: string;
    selectCustomerFirst: string; default: string;
    products: string; addProduct: string; productName: string;
    sku: string; quantity: string; noProducts: string; productNamePlaceholder: string;
    notes: string; notesPlaceholder: string;
    recipientSection: string; name: string; recipientName: string;
    country: string; countryPlaceholder: string; phone: string;
    district: string; districtPlaceholder: string;
    city: string; cityPlaceholder: string; neighborhood: string;
    neighborhoodPlaceholder: string; detailedAddress: string; streetPlaceholder: string;
    recipientNotes: string;
    carrierSection: string; noCarriers: string; addCarrier: string;
    shipViaCarrierAccount: string; carrierAutomatic: string;
    paymentSection: string; shipmentValue: string; amountPlaceholder: string;
    deliveryFee: string; automatic: string; total: string; currencySymbol: string;
    paymentMethod: string; paymentCash: string; paymentBankTransfer: string;
    paymentOnline: string; paymentStatus: string; paymentPending: string;
    paymentPaid: string; paymentPartial: string;
    submitButton: string; confirmTitle: string; senderSection: string;
    warehouseLabel: string; recipientConfirm: string; shipmentConfirm: string;
    carrierConfirm: string; carrierLabel: string; paymentConfirm: string;
    shipmentValueConfirm: string; riyalCurrency: string; deliveryFeeConfirm: string;
    totalAmount: string; paymentMethodConfirm: string; electronicPayment: string;
    confirmCreate: string; editData: string; submitting: string;
    fillAllFields: string; successMessage: string; errorMessage: string;
    itemNamePlaceholder: string; invalidShipmentValue: string;
    recipientNameRequired: string; phoneRequired: string; invalidPhone: string; addressRequired: string;
    cityRequired: string; districtRequired: string; neighborhoodRequired: string;
    itemsCount: string; serviceTypeConfirm: string;
    noRouteForLane: string; selectLocationForPrices: string;
    completeRequiredFields: string; noRouteCarrierError: string;
    paymentModeLabel: string;
    paymentModeRecipientPaysAll: string;
    paymentModeRecipientPaysFee: string;
    paymentModeSenderPaysAll: string;
  };
  settings: {
    pageTitle: string; pageDescription: string;
    tabGeneral: string; tabNotifications: string; tabSecurity: string;
    tabAppearance: string; tabIntegration: string; tabDatabase: string;
    generalTitle: string; companyNameLabel: string; companyNamePlaceholder: string;
    emailLabel: string; phoneLabel: string; phonePlaceholder: string;
    timezoneLabel: string; addressLabel: string; addressPlaceholder: string;
    maintenanceModeLabel: string; maintenanceModeDescription: string;
    allowRegistrationLabel: string; allowRegistrationDescription: string;
    saveChangesButton: string;
    emailNotificationsTitle: string; newShipmentLabel: string; newShipmentDescription: string;
    shipmentStatusLabel: string; shipmentStatusDescription: string;
    lateShipmentLabel: string; lateShipmentDescription: string;
    dailyReportsLabel: string; dailyReportsDescription: string;
    smsNotificationsTitle: string; deliveryConfirmationLabel: string;
    deliveryConfirmationDescription: string; completionConfirmationLabel: string;
    completionConfirmationDescription: string; verificationCodeLabel: string;
    verificationCodeDescription: string;
    securityTitle: string; twoFactorLabel: string; twoFactorDescription: string;
    strongPasswordLabel: string; strongPasswordDescription: string;
    sessionTimeoutLabel: string; sessionTimeoutDescription: string;
    loginAttemptsLabel: string; loginAttemptsDescription: string;
    activityLoggingLabel: string; activityLoggingDescription: string;
    newLoginAlertLabel: string; newLoginAlertDescription: string;
    apiKeysTitle: string; primaryApiKeyLabel: string; refreshButton: string;
    webhookSecretLabel: string;
    appearanceTitle: string; themeLabel: string; themeLight: string;
    themeDark: string; themeAuto: string; languageLabel: string;
    languageArabic: string; languageEnglish: string; primaryColorLabel: string;
    companyLogoLabel: string; uploadButton: string;
    collapsedSidebarLabel: string; collapsedSidebarDescription: string;
    webhooksTitle: string; webhookUrlLabel: string; webhookUrlPlaceholder: string;
    webhookUrlDescription: string; enableWebhooksLabel: string; enableWebhooksDescription: string;
    emailServiceTitle: string; smtpHostLabel: string; smtpHostPlaceholder: string;
    smtpPortLabel: string; smtpPortPlaceholder: string; smtpEncryptionLabel: string;
    encryptionNone: string; encryptionTls: string; encryptionSsl: string;
    usernameLabel: string; usernamePlaceholder: string; passwordLabel: string;
    testConnectionButton: string;
    smsServiceTitle: string; smsProviderLabel: string;
    providerTwilio: string; providerVonage: string; providerCustom: string;
    apiKeyLabel: string; senderNameLabel: string;
    backupTitle: string; autoBackupLabel: string; autoBackupDescription: string;
    backupTimeLabel: string; backupRetentionLabel: string;
    createBackupButton: string; restoreBackupButton: string;
    dangerZoneTitle: string; deleteOldDataLabel: string; deleteOldDataDescription: string;
    deleteNever: string; delete6Months: string; delete1Year: string; delete2Years: string;
    resetSystemButton: string; resetSystemWarning: string;
    saveSuccessTitle: string; saveSuccessDescription: string;
  };
  carrierCities: {
    pageTitle: string; pageDescription: string;
    stateColumnHeader: string; availableCitiesColumnHeader: string;
    selectedCitiesColumnHeader: string; editCitiesTooltip: string;
    emptyStateTitle: string; emptyStateDescription: string;
  };
  coverage: {
    pageTitle: string; pageDescription: string;
    countryColumn: string; districtColumn: string; citiesColumn: string;
    saveBtn: string; selectCountry: string; selectDistrict: string;
    saved: string; emptyCountries: string; selectAll: string; deselectAll: string;
  };
  deliveryZones: {
    pageTitle: string; pageDescription: string;
    codeColumnHeader: string; nameColumnHeader: string; cityColumnHeader: string;
    descriptionColumnHeader: string; statusColumnHeader: string;
    statusActive: string; statusInactive: string;
    viewAreasTooltip: string; editTooltip: string; deleteTooltip: string;
    addZoneButton: string; emptyStateTitle: string; emptyStateDescription: string;
    deleteConfirmTitle: string; deleteConfirmDescription: string;
    cancelButton: string; deleteSuccessTitle: string; deleteSuccessDescription: string;
    errorTitle: string; deleteErrorDescription: string;
  };
  deliveryZoneDetails: {
    pageTitle: string; pageDescription: string; notFoundMessage: string;
    removeSuccessTitle: string; removeSuccessDescription: string;
    addSuccessTitle: string; addSuccessDescription: string;
    errorTitle: string; removeErrorDescription: string; addErrorDescription: string;
  };
  routes: {
    listTitle: string; listSubtitle: string; addNewRoute: string;
    code: string; routeName: string; zone: string; distanceTime: string;
    status: string; km: string; minutes: string; active: string; inactive: string;
    viewDetails: string; edit: string; delete: string;
    noRoutes: string; noRoutesDesc: string;
    deleteSuccess: string; deleteError: string;
    deleteConfirmTitle: string; deleteConfirmDesc: string;
    detailsBack: string; copyId: string; copyIdSuccess: string;
    dataRefreshed: string; notFound: string; notFoundDesc: string; backToRoutes: string;
    estimatedDistance: string; estimatedTime: string;
    assignedDrivers: string; assignDriver: string; editRoute: string;
  };
  pricing: {
    listTitle: string; listSubtitle: string; createNew: string;
    name: string; defaultPrice: string; deliveryType: string; routesCount: string;
    viewDetails: string; delete: string;
    noLists: string; noListsDesc: string;
    deleteSuccess: string; deleteFailed: string;
    detailsTitle: string; detailsSubtitle: string;
    editList: string; cancelEdit: string;
    listName: string; defaultDeliveryFee: string; defaultDeliveryType: string;
    selectDeliveryType: string; updateSuccess: string; updateFailed: string; loadFailed: string;
    notSpecified: string; routesAndRates: string; addRoute: string;
    noRoutes: string; noRoutesDesc: string;
    fromCity: string; toCity: string; price: string; from: string; to: string;
    deleteRoute: string; deleteRateSuccess: string; deleteRateFailed: string; currency: string;
  };
  carriers: {
    listTitle: string; listSubtitle: string; addUser: string; addCarrier: string;
    approve: string; suspend: string; unsuspend: string;
    noCarriers: string; noCarriersDescription: string;
    notFound: string; errorTitle: string; backToList: string;
    detailsTitle: string; wallet: string; tenantSettlements: string; platformFees: string;
    editNotification: string;
  };
  tenants: {
    listTitle: string; listSubtitle: string; addTenant: string;
    subscription: string; integration: string; shipments: string;
    enterpriseSubscription: string; premiumSubscription: string; basicSubscription: string;
    apiIntegration: string; webhookIntegration: string; manualIntegration: string;
    noTenants: string; noTenantsDescription: string;
    notFound: string; notFoundDescription: string; backToList: string;
    disableTenant: string; enableTenant: string;
    totalShipments: string; newShipments: string; inDelivery: string; completedShipments: string;
    overview: string; warehouses: string; fulfillmentCenter: string; settlements: string;
    copiedSuccess: string; loadError: string;
  };
  users: {
    pageTitle: string; pageDescription: string; addButton: string;
    roleAdmin: string; roleCarrier: string; roleDriver: string;
    roleTenant: string; roleCustomer: string;
    statusActive: string; statusInactive: string;
    tableHeaderName: string; tableHeaderEmail: string; tableHeaderPhone: string;
    tableHeaderRole: string; tableHeaderStatus: string; tableHeaderCreatedDate: string;
    viewDetailsTooltip: string; emptyStateTitle: string; emptyStateDescription: string;
    clearFiltersButton: string;
    detailsBack: string; userNotFound: string; userNotFoundDesc: string;
    accountInfoTitle: string; emailVerificationTitle: string;
    emailVerifiedBadge: string; emailNotVerifiedBadge: string;
    emailVerifiedHint: string; emailNotVerifiedHint: string;
    markEmailVerifiedButton: string; lastLoginLabel: string; neverLoggedIn: string;
    editButton: string; editUserTitle: string;
    fieldActiveLabel: string; fieldActiveHint: string;
    saveButton: string; savingButton: string; cancelButton: string;
    updateSuccess: string;
  };
  finance: {
    pageTitle: string; pageDescription: string;
    walletServices: string; walletServicesSubtitle: string;
    walletCod: string; walletCodSubtitle: string;
    pendingCod: string; pendingCodSubtitle: string;
    netProfit: string; netProfitSubtitle: string;
    tableHeaderTrackingNumber: string; tableHeaderTransactionType: string;
    tableHeaderAmount: string; tableHeaderStatus: string; tableHeaderDate: string;
    tableHeaderDescription: string;
    transactionTypeServiceFee: string; transactionTypeCodCollection: string;
    transactionTypePayment: string; transactionTypeRefund: string;
    statusCompleted: string; statusPending: string; statusFailed: string;
    transactionsTableTitle: string; transactionsTableDescription: string;
    noTransactionsMessage: string; noTransactionsDescription: string;
    chartTitle: string;
  };
  reports: {
    pageTitle: string; pageDescription: string;
    dateRangeButton: string; exportPdfButton: string; exportButtonMobile: string;
    shipmentTrendChartTitle: string; statusDistributionChartTitle: string;
    financialReportsSection: string;
    totalRevenueLabel: string; monthComparisonLabel: string;
    totalExpensesLabel: string; netProfitLabel: string;
    pendingInvoicesLabel: string; pendingInvoicesSubtitle: string;
    codCollectedLabel: string; codCollectedSubtitle: string;
    prepaidLabel: string; prepaidSubtitle: string;
    managementReportsSection: string;
    totalShipmentsLabel: string; totalShipmentsSubtitle: string;
    activeTenantLabel: string; activeTenantSubtitle: string;
    driversLabel: string; driversSubtitle: string;
    pendingOrdersLabel: string; pendingOrdersSubtitle: string;
    completedTodayLabel: string; completedTodaySubtitle: string;
    cancelledLabel: string; cancelledSubtitle: string;
    performanceReportsSection: string;
    deliveryRateLabel: string; avgDeliveryTimeLabel: string; avgDeliveryTimeSubtitle: string;
    customerSatisfactionLabel: string; customerSatisfactionSubtitle: string;
    complaintsLabel: string; complaintsSubtitle: string;
    onTimeDeliveryLabel: string; failureRateLabel: string;
  };
  notFound: { errorCode: string; title: string; homeLink: string };
  exceptions: {
    title: string; subtitle: string;
    tabs: { logs: string; incidents: string };
    stats: { total: string; last24h: string; topModule: string; topCode: string };
    filters: {
      title: string; clear: string;
      severity: string; allSeverities: string;
      source: string; allSources: string;
      module: string; modulePlaceholder: string;
      errorCode: string; errorCodePlaceholder: string;
      from: string; to: string;
    };
    columns: {
      time: string; code: string; severity: string; module: string;
      message: string; origin: string; source: string;
      lastSeen: string; occurrences: string; firstSeen: string;
      status: string; resolved: string; open: string;
    };
    detail: {
      message: string; origin: string; function: string;
      request: string; userId: string; tenantId: string; ipAddress: string;
      details: string; stackTrace: string;
      requestBody: string; requestQuery: string; requestHeaders: string;
      sqlHelper: string; sqlCopied: string;
      correlationIdCopied: string; recentLogs: string;
    };
    resolve: {
      title: string; notePlaceholder: string;
      button: string; resolving: string; success: string;
    };
    emptyLogs: string; emptyLogsDesc: string;
    emptyIncidents: string; emptyIncidentsDesc: string;
  };
  outstandingFees: {
    pageTitle: string; pageSubtitleCarrier: string; pageSubtitleTenant: string;
    refresh: string;
    totalOwed: string; shipmentsCount: string; tenantsCount: string; shipmentsLabel: string;
    markAllPaid: string; markPaid: string;
    markPaidSuccess: string; markPaidError: string; loadError: string;
    emptyTitle: string; emptyDescription: string;
  };
  support: {
    pageTitle: string; pageSubtitle: string;
    contactTitle: string; contactDescription: string;
    whatsappLabel: string; whatsappAction: string;
    emailLabel: string; phoneLabel: string;
    hoursLabel: string; hoursValue: string;
    formTitle: string; formDescription: string;
    categoryLabel: string; categoryPlaceholder: string;
    subjectLabel: string; subjectPlaceholder: string;
    relatedRefLabel: string; relatedRefPlaceholder: string;
    messageLabel: string; messagePlaceholder: string;
    submitButton: string; submitting: string;
    successTitle: string; successMessage: string; sendAnother: string;
    errorMessage: string; validationError: string;
    faqTitle: string;
    tenantCategories: string[]; carrierCategories: string[];
    tenantFaq: { q: string; a: string }[];
    carrierFaq: { q: string; a: string }[];
  };
}

export const pagesAr: PageTranslations = {
  commonExt: {
    clearFilters: 'مسح الفلاتر', viewDetails: 'عرض التفاصيل', refresh: 'تحديث',
    copyId: 'نسخ المعرّف', editData: 'تعديل البيانات', active: 'نشط', inactive: 'غير نشط',
    suspended: 'معلق', name: 'الاسم', email: 'البريد الإلكتروني', phone: 'الهاتف',
    status: 'الحالة', type: 'النوع', updateSuccess: 'تم التحديث بنجاح',
    updateError: 'فشل التحديث', refreshSuccess: 'تم تحديث البيانات', close: 'إغلاق',
    add: 'إضافة', create: 'إنشاء', print: 'طباعة', exportLabel: 'تصدير',
    saveChanges: 'حفظ التغييرات', testConnection: 'اختبار الاتصال', upload: 'رفع',
    required: 'مطلوب', notSpecified: 'غير محدد', pending: 'قيد الانتظار',
    yes: 'نعم', no: 'لا', deleteConfirm: 'تأكيد الحذف',
    cannotUndo: 'لا يمكن التراجع عن هذا الإجراء', copy: 'نسخ',
  },
  dashboard: { welcomeMessage: 'مرحباً، {name}' },
  drivers: {
    pageTitle: 'السائقين', pageSubtitle: 'إدارة مناديب التوصيل', addDriverButton: 'إضافة سائق',
    tableHeaderName: 'الاسم', tableHeaderPhone: 'الهاتف', tableHeaderPaymentType: 'نوع الدفع',
    paymentTypeCommission: 'عمولة', paymentTypeSalary: 'راتب',
    tableHeaderCommissionRate: 'نسبة العمولة', tableHeaderCreatedDate: 'تاريخ الإنشاء',
    viewDetailsTooltip: 'عرض التفاصيل', emptyStateTitle: 'لا يوجد سائقين',
    emptyStateDescription: 'لم يتم العثور على أي سائقين تطابق معايير البحث',
    driverIdCopied: 'تم نسخ معرّف السائق', driverNotFound: 'السائق غير موجود',
    driverNotFoundDescription: 'لم يتم العثور على السائق المطلوب',
    backToListButton: 'العودة للقائمة', statusActive: 'نشط', statusInactive: 'غير نشط',
    copyIdButton: 'نسخ المعرّف', currentShipmentsLabel: 'الشحنات الحالية',
    shipmentUnitLabel: 'شحنة', availableLabel: 'المتاح', usedCapacityLabel: 'الحجم المستخدم',
    cubicMeterUnit: 'م³', usedWeightLabel: 'الوزن المستخدم', kilogramUnit: 'كجم',
    tabOverview: 'نظرة عامة', tabZones: 'المناطق', tabRoutes: 'المسارات',
    tabShipments: 'الشحنات', tabLocation: 'الموقع', tabHistory: 'السجل', tabFinancial: 'المالية',
    editDataMenuItem: 'تعديل البيانات', disableDriverMenuItem: 'تعطيل السائق',
    enableDriverMenuItem: 'تفعيل السائق', assignShipmentMenuItem: 'تعيين شحنة',
    shipmentsListTitle: 'قائمة الشحنات',
    shipmentsListDescription: 'عرض جميع الشحنات المسندة لهذا السائق',
    viewShipmentsButton: 'عرض الشحنات', trackLocationTitle: 'تتبع الموقع',
    trackLocationDescription: 'عرض الموقع الحالي للسائق على الخريطة',
    activityHistoryTitle: 'سجل النشاط',
    activityHistoryDescription: 'عرض سجل جميع الأنشطة والتغييرات',
    errorLoadingDriverData: 'فشل في تحميل بيانات السائق',
    delegateLabel: 'مندوب:', latitudeLabel: 'خط العرض:', longitudeLabel: 'خط الطول:',
    lastUpdateLabel: 'آخر تحديث:', trackingPageTitle: 'تتبع المندوبين',
    connectedStatus: 'متصل', disconnectedStatus: 'غير متصل',
    activeDriversLabel: 'المندوبين النشطين:', noActiveDrivers: 'لا يوجد مندوبين نشطين حالياً',
    sinceLabel: 'منذ', activeDriverStat: 'مندوب نشط',
    secondsUnit: 'ثانية', minuteUnit: 'دقيقة', hourUnit: 'ساعة',
  },
  shipments: {
    pageTitle: 'الشحنات', pageSubtitle: 'إدارة وتتبع الشحنات', addNew: 'إضافة شحنة جديدة',
    trackingNumber: 'رقم التتبع', status: 'الحالة', address: 'العنوان', recipient: 'المستلم',
    serviceType: 'نوع الخدمة', value: 'القيمة', date: 'التاريخ', viewDetails: 'عرض التفاصيل',
    emptyTitle: 'لا توجد شحنات',
    emptyDescription: 'لم يتم العثور على أي شحنات تطابق معايير البحث',
    clearFilters: 'مسح الفلاتر', pagination: 'صفحة {page} من {totalPages}',
    previous: 'السابق', next: 'التالي',
  },
  shipmentDetails: {
    back: 'رجوع', copy: 'نسخ', print: 'طباعة', retryDelivery: 'إعادة محاولة التوصيل',
    assignDriver: 'تعيين سائق', deliveredBadge: 'تم التسليم', driverSettled: 'تسوية السائق',
    tenantSettled: 'تسوية العميل', codCollection: 'تحصيل COD', pending: 'قيد الانتظار',
    carrier: 'الناقل', fromCity: 'مدينة الإرسال', toCity: 'مدينة التسليم',
    piecesCount: 'عدد القطع', createdDate: 'تاريخ الإنشاء',
    expectedDeliveryDate: 'تاريخ التسليم المتوقع', actualDeliveryDate: 'تاريخ التسليم الفعلي',
    serviceType: 'نوع الخدمة', paymentType: 'نوع الدفع', paymentStatus: 'حالة الدفع',
    codAmount: 'المبلغ المطلوب تحصيله', shipmentValue: 'قيمة البضاعة',
    platformFee: 'رسوم المنصة', deliveryFees: 'رسوم التوصيل',
    tenantPayout: 'مستحقات العميل', total: 'الإجمالي', shipmentInfo: 'بيانات الشحنة',
    customer: 'العميل', sender: 'المرسل', warehouseLocation: 'موقع المستودع',
    senderAddress: 'عنوان المرسل', recipientAddress: 'عنوان المستلم',
    items: 'العناصر ({count})', itemName: 'اسم', itemQuantity: 'الكمية', itemSku: 'رقم المنتج',
    trips: 'الرحلات ({count})', loading: 'جاري التحميل...', noTrips: 'لا توجد رحلات مسجلة',
    tripSequence: '#', tripType: 'نوع الرحلة', tripRoute: 'المسار', tripDriver: 'السائق',
    manifest: 'القائمة', attempt: 'المحاولة {number}', unassigned: 'غير معين',
    viewTrip: 'عرض الرحلة', descriptionTab: 'الوصف', historyTab: 'الحركات',
    notificationsTab: 'الإشعارات', timeline: 'السجل الزمني للشحنة', noHistory: 'لا يوجد سجل زمني',
    noNotifications: 'لا توجد إشعارات لهذه الشحنة', notificationsCount: 'الإشعارات ({count})',
    notificationType: 'النوع', notificationChannels: 'القنوات', notificationRecipient: 'المستلم',
    notificationTitle: 'العنوان', channelInApp: 'داخل التطبيق', channelWhatsapp: 'واتساب',
    channelEmail: 'بريد', channelSms: 'رسالة نصية',
    recipientRoleTenant: 'العميل', recipientRoleCarrier: 'الناقل',
    recipientRoleDriver: 'السائق', recipientRoleAdmin: 'المسؤول',
    recipientRoleFinal: 'المستلم النهائي', assignDriverTitle: 'تعيين سائق',
    noDriversAvailable: 'لا يوجد سائقين متاحين', currentShipments: 'عدد الشحنات الحالية:',
    coverageAreas: 'مناطق التغطية:', retryDialogTitle: 'إعادة محاولة التوصيل',
    retryWarning: 'سيتم إنشاء رحلة توصيل جديدة لإعادة المحاولة',
    retryScheduledDate: 'تاريخ المحاولة الجديدة', retryNotes: 'ملاحظات',
    retryNotesPlaceholder: 'ملاحظات اختيارية...', cancel: 'إلغاء',
    scheduling: 'جاري الجدولة...', scheduleRetry: 'جدولة المحاولة',
    trackingCopied: 'تم نسخ رقم التتبع', failedLoadingDrivers: 'فشل تحميل السائقين',
    failedLoadingNotifications: 'فشل تحميل الإشعارات', failedLoadingHistory: 'فشل تحميل السجل الزمني',
    driverAssignedSuccess: 'تم تعيين السائق بنجاح', failedAssignDriver: 'فشل تعيين السائق',
    retryScheduledSuccess: 'تم جدولة محاولة توصيل جديدة',
    outForPickup: 'خرج للاستلام', collectedFromSender: 'تم الاستلام من المرسل',
    inTransit: 'في الطريق', outForDelivery: 'خرج للتوصيل', delivered: 'تم التسليم',
    deliveryFailed: 'فشل التسليم', returning: 'قيد الإرجاع', returned: 'تم الإرجاع',
    pendingStatus: 'قيد الانتظار', totalTrips: 'الإجمالي:',
  },
  createShipment: {
    pageTitle: 'إنشاء شحنة جديدة', pageSubtitle: 'املأ البيانات لإنشاء شحنة',
    shipmentSection: 'الشحنة', customer: 'العميل', selectCustomer: 'اختر العميل...',
    search: 'ابحث...', required: 'مطلوب', selectCarrier: 'اختر شركة الشحن',
    serviceType: 'نوع الخدمة', selectServiceType: 'اختر نوع الخدمة...',
    warehouse: 'المستودع', loading: 'جاري التحميل...', selectWarehouse: 'اختر المستودع...',
    selectCustomerFirst: 'اختر العميل أولاً', default: 'افتراضي',
    products: 'المنتجات', addProduct: 'إضافة منتج', productName: 'المنتج',
    sku: 'SKU', quantity: 'الكمية',
    noProducts: 'لا توجد منتجات — اضغط "إضافة منتج"',
    productNamePlaceholder: 'اسم المنتج', notes: 'ملاحظات',
    notesPlaceholder: 'ملاحظات إضافية على الشحنة...',
    recipientSection: 'المستلم', name: 'الاسم', recipientName: 'اسم المستلم',
    country: 'الدولة', countryPlaceholder: 'الدولة...', phone: 'رقم الهاتف',
    district: 'الولاية', districtPlaceholder: 'الولاية...',
    city: 'المدينة', cityPlaceholder: 'المدينة...', neighborhood: 'الحي',
    neighborhoodPlaceholder: 'الحي...', detailedAddress: 'العنوان التفصيلي',
    streetPlaceholder: 'الشارع، رقم المبنى...', recipientNotes: 'ملاحظات للمستلم',
    carrierSection: 'شركة الشحن', noCarriers: 'لا توجد شركات شحن متعاقد معها',
    addCarrier: 'إضافة شركة شحن', shipViaCarrierAccount: 'الشحن عبر حسابك كناقل',
    carrierAutomatic: 'يتم تحديد الناقل تلقائياً',
    paymentSection: 'الدفع', shipmentValue: 'قيمة الشحنة', amountPlaceholder: '0.00',
    deliveryFee: 'رسوم التوصيل', automatic: 'تلقائي', total: 'المجموع',
    currencySymbol: 'ر.س', paymentMethod: 'طريقة الدفع', paymentCash: 'نقدي',
    paymentBankTransfer: 'تحويل بنكي', paymentOnline: 'إلكتروني',
    paymentStatus: 'حالة الدفع', paymentPending: 'قيد الانتظار',
    paymentPaid: 'مدفوع', paymentPartial: 'جزئي',
    submitButton: 'إنشاء الشحنة', confirmTitle: 'تأكيد إنشاء الشحنة',
    senderSection: 'المرسل', warehouseLabel: 'المستودع',
    recipientConfirm: 'المستلم', shipmentConfirm: 'الشحنة',
    carrierConfirm: 'شركة الشحن', carrierLabel: 'الناقل', paymentConfirm: 'الدفع',
    shipmentValueConfirm: 'قيمة الشحنة', riyalCurrency: 'ريال',
    deliveryFeeConfirm: 'رسوم التوصيل', totalAmount: 'المجموع الكلي',
    paymentMethodConfirm: 'طريقة الدفع', electronicPayment: 'دفع إلكتروني',
    confirmCreate: 'تأكيد وإنشاء الشحنة', editData: 'تعديل البيانات',
    submitting: 'جاري الإنشاء...', fillAllFields: 'يرجى ملء جميع الحقول المطلوبة',
    successMessage: 'تم إنشاء الشحنة بنجاح', errorMessage: 'فشل في إنشاء الشحنة',
    itemNamePlaceholder: 'اسم المنتج',
    invalidShipmentValue: 'قيمة الشحنة يجب أن تكون أكبر من صفر',
    recipientNameRequired: 'اسم المستلم مطلوب', phoneRequired: 'رقم الهاتف مطلوب',
    invalidPhone: 'رقم الهاتف غير صالح للدولة المختارة',
    addressRequired: 'العنوان مطلوب', cityRequired: 'المدينة مطلوبة',
    districtRequired: 'الولاية مطلوبة', neighborhoodRequired: 'الحي مطلوب',
    itemsCount: 'عدد المنتجات', serviceTypeConfirm: 'نوع الخدمة',
    noRouteForLane: 'لا يوجد تسعير لهذا المسار',
    selectLocationForPrices: 'اختر الوجهة ونوع الخدمة لعرض أسعار الشحن',
    completeRequiredFields: 'أكمل جميع الحقول المطلوبة لتفعيل زر الإنشاء',
    noRouteCarrierError: 'شركة الشحن المختارة لا تخدم هذا المسار',
    paymentModeLabel: 'من يدفع ماذا؟',
    paymentModeRecipientPaysAll: 'المستلم يدفع الكل (البضاعة + التوصيل)',
    paymentModeRecipientPaysFee: 'المستلم يدفع التوصيل فقط (البضاعة مدفوعة مسبقاً)',
    paymentModeSenderPaysAll: 'المرسل يدفع التوصيل (المستلم لا يدفع شيئاً)',
  },
  settings: {
    pageTitle: 'إعدادات النظام', pageDescription: 'إدارة إعدادات وتكوين النظام',
    tabGeneral: 'عام', tabNotifications: 'الإشعارات', tabSecurity: 'الأمان',
    tabAppearance: 'المظهر', tabIntegration: 'التكامل', tabDatabase: 'البيانات',
    generalTitle: 'الإعدادات العامة', companyNameLabel: 'اسم الشركة',
    companyNamePlaceholder: 'اسم شركتك', emailLabel: 'البريد الإلكتروني',
    phoneLabel: 'رقم الهاتف', phonePlaceholder: '05xxxxxxxx',
    timezoneLabel: 'المنطقة الزمنية', addressLabel: 'العنوان',
    addressPlaceholder: 'عنوان الشركة الكامل', maintenanceModeLabel: 'وضع الصيانة',
    maintenanceModeDescription: 'تفعيل وضع الصيانة للنظام',
    allowRegistrationLabel: 'السماح بالتسجيل',
    allowRegistrationDescription: 'السماح للمستخدمين الجدد بالتسجيل',
    saveChangesButton: 'حفظ التغييرات',
    emailNotificationsTitle: 'إشعارات البريد الإلكتروني',
    newShipmentLabel: 'شحنة جديدة',
    newShipmentDescription: 'إرسال بريد عند إنشاء شحنة جديدة',
    shipmentStatusLabel: 'تحديث حالة الشحنة',
    shipmentStatusDescription: 'إرسال بريد عند تغيير حالة الشحنة',
    lateShipmentLabel: 'شحنة متأخرة', lateShipmentDescription: 'تنبيه عند تأخر الشحنة',
    dailyReportsLabel: 'التقارير اليومية',
    dailyReportsDescription: 'إرسال تقرير يومي بالإحصائيات',
    smsNotificationsTitle: 'إشعارات الرسائل النصية',
    deliveryConfirmationLabel: 'تأكيد الاستلام',
    deliveryConfirmationDescription: 'إرسال رسالة للعميل عند الاستلام',
    completionConfirmationLabel: 'تأكيد التوصيل',
    completionConfirmationDescription: 'إرسال رسالة عند اكتمال التوصيل',
    verificationCodeLabel: 'كود التحقق',
    verificationCodeDescription: 'إرسال كود التحقق عبر الرسائل',
    securityTitle: 'إعدادات الأمان', twoFactorLabel: 'المصادقة الثنائية',
    twoFactorDescription: 'تفعيل المصادقة الثنائية لجميع المستخدمين',
    strongPasswordLabel: 'كلمة مرور قوية',
    strongPasswordDescription: 'إجبار المستخدمين على استخدام كلمات مرور قوية',
    sessionTimeoutLabel: 'مهلة الجلسة (بالدقائق)',
    sessionTimeoutDescription: 'تسجيل الخروج التلقائي بعد فترة من عدم النشاط',
    loginAttemptsLabel: 'عدد محاولات تسجيل الدخول',
    loginAttemptsDescription: 'عدد المحاولات الفاشلة قبل حظر الحساب',
    activityLoggingLabel: 'تسجيل الأنشطة',
    activityLoggingDescription: 'حفظ سجل بجميع أنشطة المستخدمين',
    newLoginAlertLabel: 'إشعار تسجيل دخول جديد',
    newLoginAlertDescription: 'إرسال إشعار عند تسجيل دخول من جهاز جديد',
    apiKeysTitle: 'مفاتيح API', primaryApiKeyLabel: 'مفتاح API الرئيسي',
    refreshButton: 'تجديد', webhookSecretLabel: 'سر Webhook',
    appearanceTitle: 'المظهر والشكل', themeLabel: 'السمة',
    themeLight: 'فاتح', themeDark: 'داكن', themeAuto: 'تلقائي',
    languageLabel: 'اللغة', languageArabic: 'العربية', languageEnglish: 'English',
    primaryColorLabel: 'اللون الأساسي', companyLogoLabel: 'شعار الشركة',
    uploadButton: 'رفع', collapsedSidebarLabel: 'الشريط الجانبي المطوي',
    collapsedSidebarDescription: 'البدء بالشريط الجانبي مطوياً',
    webhooksTitle: 'Webhooks', webhookUrlLabel: 'رابط Webhook',
    webhookUrlPlaceholder: 'https://your-domain.com/webhook',
    webhookUrlDescription: 'سيتم إرسال الإشعارات إلى هذا الرابط',
    enableWebhooksLabel: 'تفعيل Webhooks',
    enableWebhooksDescription: 'إرسال إشعارات للأحداث المهمة',
    emailServiceTitle: 'خدمة البريد الإلكتروني',
    smtpHostLabel: 'خادم SMTP', smtpHostPlaceholder: 'smtp.gmail.com',
    smtpPortLabel: 'المنفذ', smtpPortPlaceholder: '587',
    smtpEncryptionLabel: 'التشفير', encryptionNone: 'بدون',
    encryptionTls: 'TLS', encryptionSsl: 'SSL',
    usernameLabel: 'اسم المستخدم', usernamePlaceholder: 'your-email@gmail.com',
    passwordLabel: 'كلمة المرور', testConnectionButton: 'اختبار الاتصال',
    smsServiceTitle: 'خدمة الرسائل النصية', smsProviderLabel: 'مزود الخدمة',
    providerTwilio: 'Twilio', providerVonage: 'Vonage', providerCustom: 'مخصص',
    apiKeyLabel: 'مفتاح API', senderNameLabel: 'اسم المرسل',
    backupTitle: 'النسخ الاحتياطي', autoBackupLabel: 'النسخ الاحتياطي التلقائي',
    autoBackupDescription: 'إنشاء نسخة احتياطية يومياً',
    backupTimeLabel: 'وقت النسخ الاحتياطي', backupRetentionLabel: 'مدة الاحتفاظ (أيام)',
    createBackupButton: 'إنشاء نسخة احتياطية الآن',
    restoreBackupButton: 'استعادة من نسخة احتياطية',
    dangerZoneTitle: 'المنطقة الخطرة', deleteOldDataLabel: 'حذف البيانات القديمة',
    deleteOldDataDescription: 'حذف الشحنات المكتملة الأقدم من:',
    deleteNever: 'عدم الحذف', delete6Months: '6 أشهر',
    delete1Year: 'سنة', delete2Years: 'سنتين',
    resetSystemButton: 'إعادة تعيين النظام',
    resetSystemWarning: '⚠️ تحذير: هذا الإجراء سيحذف جميع البيانات ولا يمكن التراجع عنه',
    saveSuccessTitle: 'تم الحفظ', saveSuccessDescription: 'تم حفظ الإعدادات بنجاح',
  },
  carrierCities: {
    pageTitle: 'مدن التوصيل',
    pageDescription: 'تحديد المدن والمناطق التي يغطيها الناقل',
    stateColumnHeader: 'الولاية', availableCitiesColumnHeader: 'المدن المتاحة',
    selectedCitiesColumnHeader: 'المدن المحددة', editCitiesTooltip: 'تعديل المدن',
    emptyStateTitle: 'لا توجد ولايات', emptyStateDescription: 'لا توجد بيانات ولايات في النظام',
  },
  coverage: {
    pageTitle: 'تغطية التوصيل',
    pageDescription: 'تحديد المدن التي يغطيها الناقل',
    countryColumn: 'الدولة', districtColumn: 'المنطقة', citiesColumn: 'المدن',
    saveBtn: 'حفظ التغطية', selectCountry: 'اختر دولة', selectDistrict: 'اختر منطقة',
    saved: 'تم حفظ التغطية', emptyCountries: 'لا توجد دول متاحة',
    selectAll: 'تحديد الكل', deselectAll: 'إلغاء تحديد الكل',
  },
  deliveryZones: {
    pageTitle: 'مناطق التوصيل',
    pageDescription: 'إدارة مناطق التوصيل والأحياء التابعة لها',
    codeColumnHeader: 'الرمز', nameColumnHeader: 'الاسم', cityColumnHeader: 'المدينة',
    descriptionColumnHeader: 'الوصف', statusColumnHeader: 'الحالة',
    statusActive: 'نشط', statusInactive: 'غير نشط',
    viewAreasTooltip: 'عرض الأحياء', editTooltip: 'تعديل', deleteTooltip: 'حذف',
    addZoneButton: 'إضافة منطقة جديدة', emptyStateTitle: 'لا توجد مناطق',
    emptyStateDescription: 'لم يتم إضافة أي مناطق توصيل بعد',
    deleteConfirmTitle: 'تأكيد الحذف',
    deleteConfirmDescription: 'هل أنت متأكد من حذف منطقة "{name}"؟ لا يمكن التراجع عن هذا الإجراء.',
    cancelButton: 'إلغاء', deleteSuccessTitle: 'تم الحذف',
    deleteSuccessDescription: 'تم حذف منطقة التوصيل بنجاح',
    errorTitle: 'خطأ', deleteErrorDescription: 'فشل في حذف منطقة التوصيل',
  },
  deliveryZoneDetails: {
    pageTitle: 'مناطق التوصيل', pageDescription: 'عرض وإدارة أحياء منطقة {name}',
    notFoundMessage: 'لم يتم العثور على المنطقة',
    removeSuccessTitle: 'تم الإزالة', removeSuccessDescription: 'تم إزالة الحي من المنطقة بنجاح',
    addSuccessTitle: 'تمت الإضافة', addSuccessDescription: 'تم إضافة الحي إلى المنطقة بنجاح',
    errorTitle: 'خطأ', removeErrorDescription: 'فشل في إزالة الحي',
    addErrorDescription: 'فشل في إضافة الحي',
  },
  routes: {
    listTitle: 'مسارات التوصيل', listSubtitle: 'إدارة مسارات التوصيل',
    addNewRoute: 'إضافة مسار جديد', code: 'الكود', routeName: 'اسم المسار',
    zone: 'من → إلى', distanceTime: 'المسافة / الوقت', status: 'الحالة',
    km: 'كم', minutes: 'دقيقة', active: 'نشط', inactive: 'غير نشط',
    viewDetails: 'عرض التفاصيل', edit: 'تعديل', delete: 'حذف',
    noRoutes: 'لا توجد مسارات', noRoutesDesc: 'لم يتم إنشاء أي مسارات بعد',
    deleteSuccess: 'تم حذف المسار بنجاح',
    deleteError: 'حدث خطأ أثناء حذف المسار',
    deleteConfirmTitle: 'حذف المسار',
    deleteConfirmDesc: 'هل أنت متأكد من حذف المسار "{name}"؟ سيتم إلغاء تفعيل المسار.',
    detailsBack: 'المسارات', copyId: 'نسخ المعرف', copyIdSuccess: 'تم نسخ المعرف',
    dataRefreshed: 'تم تحديث البيانات', notFound: 'المسار غير موجود',
    notFoundDesc: 'لم يتم العثور على المسار المطلوب', backToRoutes: 'العودة للمسارات',
    estimatedDistance: 'المسافة التقديرية', estimatedTime: 'الوقت التقديري',
    assignedDrivers: 'السائقون المعينون', assignDriver: 'تعيين سائق',
    editRoute: 'تعديل المسار',
  },
  pricing: {
    listTitle: 'قوائم الأسعار',
    listSubtitle: 'إدارة قوائم أسعار التوصيل وتعيينها للعملاء',
    createNew: 'إنشاء قائمة جديدة', name: 'الاسم', defaultPrice: 'السعر الافتراضي',
    deliveryType: 'نوع التوصيل', routesCount: 'عدد المسارات',
    viewDetails: 'عرض التفاصيل', delete: 'حذف',
    noLists: 'لا توجد قوائم أسعار',
    noListsDesc: 'لم يتم إنشاء أي قوائم أسعار بعد. أنشئ قائمة جديدة للبدء.',
    deleteSuccess: 'تم حذف قائمة الأسعار بنجاح', deleteFailed: 'فشل حذف قائمة الأسعار',
    detailsTitle: 'قائمة الأسعار', detailsSubtitle: 'إدارة مسارات وأسعار التوصيل',
    editList: 'تعديل القائمة', cancelEdit: 'إلغاء التعديل',
    listName: 'اسم القائمة', defaultDeliveryFee: 'السعر الافتراضي',
    defaultDeliveryType: 'نوع التوصيل الافتراضي', selectDeliveryType: 'اختر نوع التوصيل',
    updateSuccess: 'تم تحديث قائمة الأسعار', updateFailed: 'فشل تحديث قائمة الأسعار',
    loadFailed: 'فشل تحميل قائمة الأسعار', notSpecified: 'غير محدد',
    routesAndRates: 'المسارات والأسعار', addRoute: 'إضافة مسار',
    noRoutes: 'لا توجد مسارات',
    noRoutesDesc: 'لم يتم إضافة أي مسارات بعد. سيتم استخدام السعر الافتراضي لجميع المسارات.',
    fromCity: 'من مدينة', toCity: 'إلى مدينة', price: 'السعر',
    from: 'من:', to: 'إلى:', deleteRoute: 'حذف',
    deleteRateSuccess: 'تم حذف السعر', deleteRateFailed: 'فشل حذف السعر',
    currency: 'ج.س',
  },
  carriers: {
    listTitle: 'الناقلين', listSubtitle: 'إدارة شركات الشحن',
    addUser: 'إضافة مستخدم', addCarrier: 'إضافة ناقل',
    approve: 'قبول', suspend: 'تعليق', unsuspend: 'رفع التعليق',
    noCarriers: 'لا يوجد ناقلين',
    noCarriersDescription: 'لم يتم العثور على ناقلين. ابدأ بإضافة ناقل جديد.',
    notFound: 'لم يتم العثور على الناقل', errorTitle: 'خطأ',
    backToList: 'العودة إلى قائمة الناقلين', detailsTitle: 'تفاصيل الناقل',
    wallet: 'المحفظة', tenantSettlements: 'تسويات الشركاء',
    platformFees: 'رسوم المنصة', editNotification: 'جاري فتح نافذة التعديل...',
  },
  tenants: {
    listTitle: 'الشركاء', listSubtitle: 'إدارة العملاء والشركاء',
    addTenant: 'إضافة شريك', subscription: 'الاشتراك', integration: 'التكامل',
    shipments: 'الشحنات', enterpriseSubscription: 'مؤسسات',
    premiumSubscription: 'مميز', basicSubscription: 'أساسي',
    apiIntegration: 'API', webhookIntegration: 'Webhook', manualIntegration: 'يدوي',
    noTenants: 'لا يوجد شركاء',
    noTenantsDescription: 'لم يتم العثور على أي شركاء تطابق معايير البحث',
    notFound: 'الشريك غير موجود',
    notFoundDescription: 'لم يتم العثور على الشريك المطلوب',
    backToList: 'العودة للقائمة',
    disableTenant: 'تعطيل الشريك', enableTenant: 'تفعيل الشريك',
    totalShipments: 'إجمالي الشحنات', newShipments: 'جديدة',
    inDelivery: 'قيد التوصيل', completedShipments: 'مكتملة',
    overview: 'نظرة عامة', warehouses: 'المستودعات',
    fulfillmentCenter: 'مركز الفرز', settlements: 'التسويات',
    copiedSuccess: 'تم نسخ معرّف الشريك',
    loadError: 'فشل في تحميل بيانات الشريك',
  },
  users: {
    pageTitle: 'المستخدمين', pageDescription: 'إدارة حسابات المستخدمين',
    addButton: 'إضافة مستخدم', roleAdmin: 'مدير النظام',
    roleCarrier: 'ناقل', roleDriver: 'سائق',
    roleTenant: 'عميل', roleCustomer: 'مستلم',
    statusActive: 'نشط', statusInactive: 'غير نشط',
    tableHeaderName: 'الاسم', tableHeaderEmail: 'البريد الإلكتروني',
    tableHeaderPhone: 'الهاتف', tableHeaderRole: 'الدور',
    tableHeaderStatus: 'الحالة', tableHeaderCreatedDate: 'تاريخ الإنشاء',
    viewDetailsTooltip: 'عرض التفاصيل', emptyStateTitle: 'لا يوجد مستخدمين',
    emptyStateDescription: 'لم يتم العثور على أي مستخدمين تطابق معايير البحث',
    clearFiltersButton: 'مسح الفلاتر',
    detailsBack: 'العودة لقائمة المستخدمين',
    userNotFound: 'المستخدم غير موجود',
    userNotFoundDesc: 'لم يتم العثور على المستخدم المطلوب',
    accountInfoTitle: 'معلومات الحساب',
    emailVerificationTitle: 'توثيق البريد الإلكتروني',
    emailVerifiedBadge: 'موثّق', emailNotVerifiedBadge: 'غير موثّق',
    emailVerifiedHint: 'أكّد هذا المستخدم عنوان بريده الإلكتروني.',
    emailNotVerifiedHint: 'لم يؤكّد هذا المستخدم بريده الإلكتروني بعد ولا يمكنه تسجيل الدخول.',
    markEmailVerifiedButton: 'تعليم البريد كموثّق',
    lastLoginLabel: 'آخر تسجيل دخول', neverLoggedIn: 'لم يسجّل الدخول بعد',
    editButton: 'تعديل', editUserTitle: 'تعديل بيانات المستخدم',
    fieldActiveLabel: 'الحساب نشط',
    fieldActiveHint: 'يمكن للمستخدم تسجيل الدخول واستخدام الحساب.',
    saveButton: 'حفظ التغييرات', savingButton: 'جارٍ الحفظ...', cancelButton: 'إلغاء',
    updateSuccess: 'تم تحديث بيانات المستخدم',
  },
  finance: {
    pageTitle: 'المالية', pageDescription: 'إدارة المحافظ والحركات المالية',
    walletServices: 'محفظة الخدمات', walletServicesSubtitle: 'الرصيد المتاح للسحب',
    walletCod: 'محفظة الكاش', walletCodSubtitle: 'المحصل من الكاش',
    pendingCod: 'كاش قيد التحصيل', pendingCodSubtitle: 'في انتظار التسليم',
    netProfit: 'صافي الربح', netProfitSubtitle: 'الإيرادات - المصروفات',
    tableHeaderTrackingNumber: 'رقم التتبع', tableHeaderTransactionType: 'نوع المعاملة',
    tableHeaderAmount: 'المبلغ', tableHeaderStatus: 'الحالة',
    tableHeaderDate: 'التاريخ', tableHeaderDescription: 'الوصف',
    transactionTypeServiceFee: 'رسوم خدمة',
    transactionTypeCodCollection: 'تحصيل عند التوصيل',
    transactionTypePayment: 'دفع', transactionTypeRefund: 'استرجاع',
    statusCompleted: 'مكتمل', statusPending: 'قيد الانتظار', statusFailed: 'فشل',
    transactionsTableTitle: 'الحركة المالية',
    transactionsTableDescription: 'جميع المعاملات المالية للشحنات',
    noTransactionsMessage: 'لا توجد معاملات مالية',
    noTransactionsDescription: 'لم يتم تسجيل أي معاملات مالية حتى الآن',
    chartTitle: 'الإيرادات وCOD — آخر 30 يوم',
  },
  reports: {
    pageTitle: 'التقارير',
    pageDescription: 'تقارير شاملة للأداء المالي والإداري',
    dateRangeButton: 'اختر الفترة', exportPdfButton: 'تصدير PDF',
    exportButtonMobile: 'تصدير',
    shipmentTrendChartTitle: 'حجم الشحنات — آخر 30 يوم',
    statusDistributionChartTitle: 'توزيع الحالات',
    financialReportsSection: 'التقارير المالية',
    totalRevenueLabel: 'إجمالي الإيرادات', monthComparisonLabel: 'عن الشهر الماضي',
    totalExpensesLabel: 'إجمالي المصروفات', netProfitLabel: 'صافي الربح',
    pendingInvoicesLabel: 'الفواتير المعلقة', pendingInvoicesSubtitle: 'فاتورة معلقة',
    codCollectedLabel: 'الدفع عند الاستلام',
    codCollectedSubtitle: 'المبالغ المحصلة نقداً',
    prepaidLabel: 'المدفوع مسبقاً', prepaidSubtitle: 'المدفوعات الإلكترونية',
    managementReportsSection: 'التقارير الإدارية',
    totalShipmentsLabel: 'إجمالي الشحنات', totalShipmentsSubtitle: 'في الفترة المحددة',
    activeTenantLabel: 'الشركاء النشطين', activeTenantSubtitle: 'شريك نشط حالياً',
    driversLabel: 'السائقين', driversSubtitle: 'سائق متاح للعمل',
    pendingOrdersLabel: 'الطلبات المعلقة', pendingOrdersSubtitle: 'في انتظار التوصيل',
    completedTodayLabel: 'المكتملة اليوم', completedTodaySubtitle: 'تم توصيلها اليوم',
    cancelledLabel: 'الملغاة', cancelledSubtitle: 'شحنات ملغاة',
    performanceReportsSection: 'تقارير الأداء',
    deliveryRateLabel: 'معدل التسليم',
    avgDeliveryTimeLabel: 'متوسط وقت التوصيل',
    avgDeliveryTimeSubtitle: 'من الاستلام للتوصيل',
    customerSatisfactionLabel: 'رضا العملاء',
    customerSatisfactionSubtitle: 'من 5 نجوم',
    complaintsLabel: 'الشكاوى', complaintsSubtitle: 'شكوى في الفترة المحددة',
    onTimeDeliveryLabel: 'التسليم في الوقت', failureRateLabel: 'معدل الفشل',
  },
  notFound: {
    errorCode: '404', title: 'عذراً! الصفحة غير موجودة',
    homeLink: 'العودة للرئيسية',
  },
  exceptions: {
    title: 'سجلات الأخطاء', subtitle: 'مراقبة وإدارة أخطاء النظام',
    tabs: { logs: 'السجلات', incidents: 'الحوادث' },
    stats: { total: 'إجمالي الأخطاء', last24h: 'آخر 24 ساعة', topModule: 'أكثر وحدة', topCode: 'أكثر كود خطأ' },
    filters: {
      title: 'فلترة', clear: 'مسح',
      severity: 'الخطورة', allSeverities: 'جميع الخطورات',
      source: 'المصدر', allSources: 'جميع المصادر',
      module: 'الوحدة', modulePlaceholder: 'اسم الوحدة...',
      errorCode: 'كود الخطأ', errorCodePlaceholder: 'مثال: GEN_',
      from: 'من تاريخ', to: 'إلى تاريخ',
    },
    columns: {
      time: 'الوقت', code: 'الكود', severity: 'الخطورة', module: 'الوحدة',
      message: 'الرسالة', origin: 'المصدر', source: 'النوع',
      lastSeen: 'آخر ظهور', occurrences: 'التكرارات', firstSeen: 'أول ظهور',
      status: 'الحالة', resolved: 'محلول', open: 'مفتوح',
    },
    detail: {
      message: 'الرسالة', origin: 'موقع الخطأ', function: 'الدالة',
      request: 'بيانات الطلب', userId: 'معرف المستخدم', tenantId: 'معرف المستأجر', ipAddress: 'عنوان IP',
      details: 'التفاصيل', stackTrace: 'تتبع المكدس',
      requestBody: 'جسم الطلب', requestQuery: 'معاملات الاستعلام', requestHeaders: 'ترويسات الطلب',
      sqlHelper: 'استعلام SQL', sqlCopied: 'تم نسخ الاستعلام',
      correlationIdCopied: 'تم نسخ معرف الارتباط', recentLogs: 'آخر السجلات',
    },
    resolve: {
      title: 'تحديد كمحلول', notePlaceholder: 'ملاحظة الحل (اختياري)...',
      button: 'تحديد كمحلول', resolving: 'جاري الحل...', success: 'تم تحديد الحادثة كمحلولة',
    },
    emptyLogs: 'لا توجد سجلات', emptyLogsDesc: 'لم يتم العثور على سجلات تطابق الفلاتر المحددة',
    emptyIncidents: 'لا توجد حوادث', emptyIncidentsDesc: 'لم يتم العثور على حوادث تطابق الفلاتر المحددة',
  },
  outstandingFees: {
    pageTitle: 'الرسوم المستحقة',
    pageSubtitleCarrier: 'رسوم التوصيل غير المدفوعة من التجار — يمكن تعليمها كمدفوعة بعد التحويل البنكي',
    pageSubtitleTenant: 'رسوم التوصيل التي تدين بها لشركة الشحن',
    refresh: 'تحديث',
    totalOwed: 'إجمالي المستحق',
    shipmentsCount: 'عدد الشحنات',
    tenantsCount: 'عدد التجار',
    shipmentsLabel: 'شحنة',
    markAllPaid: 'تعليم الكل كمدفوع',
    markPaid: 'تعليم كمدفوع',
    markPaidSuccess: 'تم تعليم الرسوم كمدفوعة',
    markPaidError: 'تعذّر التحديث، حاول مرة أخرى',
    loadError: 'تعذّر تحميل الرسوم المستحقة',
    emptyTitle: 'لا توجد رسوم مستحقة',
    emptyDescription: 'لا توجد شحنات بدون دفع رسوم في الوقت الحالي',
  },
  support: {
    pageTitle: 'الدعم الفني',
    pageSubtitle: 'تواصل مع فريق دعم كيوبوكس أو اطّلع على الأسئلة الشائعة',
    contactTitle: 'تواصل معنا',
    contactDescription: 'فريق الدعم متاح لمساعدتك',
    whatsappLabel: 'واتساب',
    whatsappAction: 'محادثة عبر واتساب',
    emailLabel: 'البريد الإلكتروني',
    phoneLabel: 'الهاتف',
    hoursLabel: 'ساعات العمل',
    hoursValue: 'الأحد - الخميس، 9 صباحاً - 5 مساءً',
    formTitle: 'أرسل لنا رسالة',
    formDescription: 'سيصلك ردّنا على بريدك الإلكتروني المسجّل',
    categoryLabel: 'نوع الطلب',
    categoryPlaceholder: 'اختر نوع الطلب',
    subjectLabel: 'الموضوع',
    subjectPlaceholder: 'عنوان مختصر لطلبك',
    relatedRefLabel: 'رقم مرتبط (اختياري)',
    relatedRefPlaceholder: 'رقم تتبع أو طلب ذي صلة',
    messageLabel: 'الرسالة',
    messagePlaceholder: 'اشرح مشكلتك أو استفسارك بالتفصيل...',
    submitButton: 'إرسال',
    submitting: 'جارٍ الإرسال...',
    successTitle: 'تم استلام رسالتك',
    successMessage: 'شكراً لتواصلك معنا. سيردّ عليك فريق الدعم في أقرب وقت.',
    sendAnother: 'إرسال رسالة أخرى',
    errorMessage: 'تعذّر إرسال رسالتك. حاول مرة أخرى.',
    validationError: 'يرجى ملء نوع الطلب والموضوع والرسالة',
    faqTitle: 'الأسئلة الشائعة',
    tenantCategories: [
      'الشحنات والتتبع',
      'الدفع عند الاستلام والمستحقات',
      'الحساب وتسجيل الدخول',
      'مشكلة في التطبيق',
      'أخرى',
    ],
    carrierCategories: [
      'الحساب والتفعيل',
      'المحفظة والفواتير',
      'السائقون والعمليات',
      'التسويات والمالية',
      'مشكلة في التطبيق',
      'اقتراح ميزة',
      'أخرى',
    ],
    tenantFaq: [
      { q: 'كيف أنشئ شحنة جديدة؟', a: 'من قائمة "الشحنات" اضغط "إضافة شحنة جديدة"، ثم أدخل بيانات المستلم والطرد واختر نوع الخدمة وأكّد الطلب.' },
      { q: 'كيف أتتبّع شحنة؟', a: 'افتح قائمة "الشحنات" وستجد حالة كل شحنة محدّثة لحظياً. يمكنك أيضاً مشاركة رابط التتبع العام مع عميلك.' },
      { q: 'متى أستلم مستحقات الدفع عند الاستلام؟', a: 'المبالغ المحصّلة نقداً تُسوّى لك من شركة الشحن وفق دورة التسويات الخاصة بها — راجع صفحة "التسويات" للتفاصيل.' },
      { q: 'كيف أطبع ملصق الشحن أو الباركود؟', a: 'من صفحة تفاصيل الشحنة اضغط "طباعة"، أو استخدم "الباركود ← طباعة" لطباعة دفعة كاملة.' },
      { q: 'لماذا تم إرجاع شحنتي؟', a: 'تُرجَع الشحنة بعد فشل محاولات التوصيل أو رفض المستلم — افتح السجل الزمني للشحنة لمعرفة السبب.' },
      { q: 'كيف أضيف مستودع استلام؟', a: 'من "الإعدادات ← المستودعات" أضف عنوان الاستلام، وسيصبح متاحاً للاختيار عند إنشاء الشحنات.' },
    ],
    carrierFaq: [
      { q: 'كيف أضيف سائقين؟', a: 'من قائمة "السائقون" اضغط "إضافة سائق"، ثم يمكنك إسناد الشحنات أو المسارات إليه.' },
      { q: 'ما فائدة المحفظة؟', a: 'تُستخدم المحفظة لتغطية رسوم منصة كيوبوكس — احرص على شحن رصيدها حتى لا تنقطع الخدمة.' },
      { q: 'كيف تعمل التسويات؟', a: 'تسويات الشركاء تدفع للتجار مستحقاتهم النقدية، وتسويات تحصيل السائقين تجمع النقد منهم، وتسويات رواتب السائقين تدفع أجورهم وعمولاتهم.' },
      { q: 'كيف أعدّ المسارات والمناطق والأسعار؟', a: 'من "الإعدادات" حدّد مناطق التوصيل والمسارات وقوائم الأسعار، ثم اربط قائمة أسعار بكل تاجر.' },
      { q: 'كيف أضيف تاجراً (شريكاً)؟', a: 'وافق على طلب تسجيله أو أضِفه من قائمة "الشركاء"، ثم اربط له قائمة أسعار ليتمكّن من الشحن.' },
      { q: 'كيف تعمل القوائم ومراكز الفرز؟', a: 'القوائم تجمع الشحنات في رحلات التقاط أو نقل أو توصيل، ومراكز الفرز تستقبلها وتفرزها وتُرسلها.' },
    ],
  },
};
